/**
 * Keyboard Shortcuts Configuration
 * 
 * Centralized configuration for all keyboard shortcuts in the application.
 * This makes it easy to manage, modify, and document shortcuts.
 */

export const SHORTCUT_KEYS = {
  TOGGLE_NAV: 'b',
  SHOW_HELP: '?',
  NAVIGATION_SEARCH: 'Shift',
  CREATE_CHAT_AGENT: 'c',
  CREATE_VOICE_AGENT: 'v',
  UPLOAD_DOCUMENT: 'd',
  ESCAPE: 'Escape',
  ARROW_UP: 'ArrowUp',
  ARROW_DOWN: 'ArrowDown',
  ENTER: 'Enter'
};

export const MODIFIER_KEYS = {
  CTRL: 'ctrlKey',
  SHIFT: 'shiftKey',
  ALT: 'altKey',
  META: 'metaKey'
};

export const TIMING = {
  DOUBLE_SHIFT_TIME: 300, // milliseconds for double shift detection
  DEBOUNCE_TIME: 50 // milliseconds for debouncing
};

/**
 * Keyboard shortcut definitions
 * Each shortcut includes:
 * - key: the key to press
 * - modifiers: required modifier keys
 * - description: human-readable description
 * - category: grouping for help display
 * - handler: function name to call (defined in handlers)
 */
export const SHORTCUTS = [
  {
    id: 'toggle-nav',
    key: SHORTCUT_KEYS.TOGGLE_NAV,
    modifiers: [MODIFIER_KEYS.CTRL],
    description: 'Toggle navigation sidebar',
    category: 'Navigation',
    handler: 'toggleNavigation'
  },
  {
    id: 'show-help',
    key: SHORTCUT_KEYS.SHOW_HELP,
    modifiers: [MODIFIER_KEYS.CTRL, MODIFIER_KEYS.SHIFT],
    description: 'Show keyboard shortcuts help',
    category: 'Help',
    handler: 'showHelp'
  },
  {
    id: 'navigation-search',
    key: SHORTCUT_KEYS.NAVIGATION_SEARCH,
    modifiers: [],
    description: 'Quick navigation search',
    category: 'Navigation',
    handler: 'showNavigationSearch',
    special: 'double-press' // Special handling for double shift
  },
  {
    id: 'create-chat-agent',
    key: SHORTCUT_KEYS.CREATE_CHAT_AGENT,
    modifiers: [MODIFIER_KEYS.CTRL, MODIFIER_KEYS.ALT],
    description: 'Create new chat agent',
    category: 'Creation',
    handler: 'createChatAgent'
  },
  {
    id: 'create-voice-agent',
    key: SHORTCUT_KEYS.CREATE_VOICE_AGENT,
    modifiers: [MODIFIER_KEYS.CTRL, MODIFIER_KEYS.ALT],
    description: 'Create new voice agent',
    category: 'Creation',
    handler: 'createVoiceAgent'
  },
  {
    id: 'upload-document',
    key: SHORTCUT_KEYS.UPLOAD_DOCUMENT,
    modifiers: [MODIFIER_KEYS.CTRL, MODIFIER_KEYS.ALT],
    description: 'Upload document to knowledge base',
    category: 'Creation',
    handler: 'uploadDocument'
  }
];

/**
 * Get shortcuts grouped by category for display purposes
 */
export const getShortcutsByCategory = () => {
  return SHORTCUTS.reduce((acc, shortcut) => {
    if (!acc[shortcut.category]) {
      acc[shortcut.category] = [];
    }
    acc[shortcut.category].push(shortcut);
    return acc;
  }, {});
};

/**
 * Format shortcut key combination for display
 */
export const formatShortcutDisplay = (shortcut) => {
  if (shortcut.special === 'double-press') {
    return `Double ${shortcut.key}`;
  }
  
  const modifierNames = {
    [MODIFIER_KEYS.CTRL]: 'Ctrl',
    [MODIFIER_KEYS.SHIFT]: 'Shift',
    [MODIFIER_KEYS.ALT]: 'Alt',
    [MODIFIER_KEYS.META]: 'Cmd'
  };
  
  const parts = shortcut.modifiers.map(mod => modifierNames[mod]).filter(Boolean);
  parts.push(shortcut.key);
  
  return parts.join('+');
};